import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Typography } from '@mui/material';
import { CheckRounded } from '@mui/icons-material';

import BottomNavbar from '../../components/shared/BottomNavbar';
import TaskCapsule from '../../components/shared/TaskCapsule';

import { TaskComponentProps } from './types';
import MESSAGES from '../../constants/message';

import imgCapsule from '/images/pharmacist.svg';
// import { ArrowUpRightIcon } from '../../components/shared/CustomIcons';

interface ListItemProps {
  title: string;
  description?: string;
  capsuleTitle: string;
  imgSrc: string;
  imgAlt?: string;
  imgWidth?: string;
  subTitle: number;
  handleClick?: () => void;
}

export const ListItem = ({
  title,
  description,
  capsuleTitle,
  imgSrc,
  imgAlt,
  imgWidth,
  subTitle,
  handleClick,
}: ListItemProps) => {
  return (
    <Box sx={{ color: 'common.white', mb: 4 }}>
      <Box px={3}>
        <Typography variant="h4" sx={{ mb: 1.5 }}>
          {title}
        </Typography>
        {description && <Typography sx={{ mb: 1.5 }}>{description}</Typography>}
      </Box>
      <TaskCapsule
        title={capsuleTitle}
        imgSrc={imgSrc}
        imgAlt={imgAlt}
        imgWidth={imgWidth}
        handleClick={handleClick}
        subTitle={`${subTitle} saved`}
      />
    </Box>
  );
};

function TaskComponent({
  tasks,
  handleTaskNavigation,
  profileURL,
}: TaskComponentProps) {
  const navigate = useNavigate();

  if (!tasks) return;

  const { medical_history, medications_count, allergies_count } = tasks;

  return (
    <Box bgcolor="primary.main" className="d-flex flex-column flex-1">
      <Box
        sx={{ gap: 2, pt: 8, px: 3, pb: 4, color: 'common.white' }}
        className="d-flex flex-column"
      >
        <Box className="d-flex align-items-center">
          <Typography variant="h4" mr="auto">
            {MESSAGES.TASK.TITLE}
          </Typography>
          <Avatar
            onClick={() => navigate('/profile')}
            alt="User"
            src={profileURL}
            className="cursor-pointer"
          />
        </Box>
        <Typography>{MESSAGES.TASK.DESCRIPTION}</Typography>
      </Box>

      {medical_history?.length > 0 && (
        <Box sx={{ gap: '1px', mb: 5 }} className="d-flex flex-column">
          {medical_history.map((task) => (
            <Box
              key={task.key}
              sx={{ bgcolor: 'ghostWhite.main', px: 3.25, py: 4 }}
              className="d-flex align-items-center rounded-50 cursor-pointer"
              onClick={() => handleTaskNavigation(task.key)}
            >
              <Box className="flex-1">
                <Typography variant="h6" mb={1.5}>
                  {task.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {task?.completed_on
                    ? MESSAGES.TASK.TASK_COMPLETED
                    : `${task?.completed_count}/${task?.total_count} completed`}
                </Typography>
              </Box>
              {task.completed_on && (
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: 'secondary.main',
                    color: 'common.white',
                  }}
                  className="d-flex justify-content-center align-items-center rounded-circle"
                >
                  <CheckRounded />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
      <ListItem
        title={MESSAGES.ALLERGY.LIST.TITLE}
        capsuleTitle={MESSAGES.ALLERGY.LIST.CAPSULE_TITLE}
        imgSrc={imgCapsule}
        imgAlt="pharmacist"
        handleClick={() => navigate('/tasks/allergies')}
        subTitle={allergies_count}
      />
      <ListItem
        title={MESSAGES.MEDICATION.LIST.TITLE}
        capsuleTitle={MESSAGES.MEDICATION.LIST.CAPSULE_TITLE}
        description={MESSAGES.MEDICATION.LIST.DESCRIPTION}
        imgSrc={imgCapsule}
        imgAlt="pharmacist"
        handleClick={() => navigate('/tasks/medications')}
        subTitle={medications_count}
      />
      {/* TODO:: 05/09/2024 :: This functionality not cover in this demo so commenting the code */}
      {/* <Box sx={{ mb: 4 }}>
        <Box sx={{ color: 'common.white', px: 3, mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 1.5 }}>
            Eye exercises
          </Typography>
          <Typography>
            Perform these exercises to relax and improve your overall eye
            health.
          </Typography>
        </Box>
        <Box
          sx={{
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '0.5px 1px',
            color: 'common.white',
          }}
          className="d-grid"
        >
          <Box
            sx={{ height: 194, maxWidth: '100%', bgcolor: 'info.main', p: 3 }}
            className="d-flex flex-column rounded-50"
          >
            <Typography variant="h6" pl={1}>
              Palming
            </Typography>
            <Box
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'tertiary.light',
                mt: 'auto',
              }}
              className="d-flex align-self-end rounded-circle cursor-pointer"
            >
              <ArrowUpRightIcon sx={{ m: 'auto' }} />
            </Box>
          </Box>
          <Box
            sx={{ height: 194, maxWidth: '100%', bgcolor: 'info.main', p: 3 }}
            className="d-flex flex-column rounded-50"
          >
            <Typography variant="h6" pl={1}>
              Blinking
            </Typography>
            <Box
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'tertiary.light',
                mt: 'auto',
              }}
              className="d-flex align-self-end rounded-circle cursor-pointer"
            >
              <ArrowUpRightIcon sx={{ m: 'auto' }} />
            </Box>
          </Box>
          <Box
            sx={{ height: 194, maxWidth: '100%', bgcolor: 'info.main', p: 3 }}
            className="d-flex flex-column rounded-50"
          >
            <Typography variant="h6" pl={1}>
              Pencil Push-Ups
            </Typography>
            <Box
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'tertiary.light',
                mt: 'auto',
              }}
              className="d-flex align-self-end rounded-circle cursor-pointer"
            >
              <ArrowUpRightIcon sx={{ m: 'auto' }} />
            </Box>
          </Box>
          <Box
            sx={{ height: 194, maxWidth: '100%', bgcolor: 'info.main', p: 3 }}
            className="d-flex flex-column rounded-50"
          >
            <Typography variant="h6" pl={1}>
              Near and far focus
            </Typography>
            <Box
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'tertiary.light',
                mt: 'auto',
              }}
              className="d-flex align-self-end rounded-circle cursor-pointer"
            >
              <ArrowUpRightIcon sx={{ m: 'auto' }} />
            </Box>
          </Box>
        </Box>
      </Box> */}
      <BottomNavbar />
    </Box>
  );
}

export default TaskComponent;
